import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import { Disclose, Headline, Loading, Refusal, Tooltip } from "@lopie/kernel-ui";

/**
 * How much of this happened without a person.
 *
 * ## Why this is not a second audit browser
 *
 * The console already has one, with filters built from the same `vocabulary`
 * block, and a second implementation of it here would be the failure
 * `packages/kernel-ui` exists to prevent. This screen deliberately shows **no
 * rows at all**.
 *
 * It answers one question with a shape instead: of the writes this kernel
 * recorded, how many had somebody in front of them. `mode` shipped with LO-55
 * and until the agent drawer nothing read it; a feed with a `mode` column
 * still does not *answer* this, because a column is something you scan and a
 * proportion is something you see. The console's job is "find me the row".
 * This one's job is "tell me the balance", and they are different tools even
 * though they read the same endpoint.
 *
 * ## Three states, and the third is not a rounding error
 *
 * `interactive`, `unattended`, and **no mode at all** — rows written before
 * LO-55, which carry neither. The kernel refuses to guess for them and so
 * does this: they are their own bar segment, their own count, and they are
 * never folded into `unattended` on the reasoning that a machine probably did
 * it. An absence is not a value. Folding it would invent the one fact `mode`
 * was added to stop inferring, on the screen built to display it.
 *
 * ## What the proportion is of
 *
 * The rows this page read, and nothing else. The endpoint pages, bounds its
 * walk, and says when it stopped early; there is no total anywhere, on
 * purpose, because a total is a full read of a table whose whole job is to
 * grow. So every number here names its window, and a run that stopped early
 * says so above the numbers rather than below them.
 *
 * The sample is newest-first, which is worth stating: this is a picture of
 * recent activity and not of the log's history. A month where everything was
 * hand-entered would not show up here if the last few hundred writes were a
 * connector sync.
 */

const WINDOW = 200;

const INTERACTIVE = "interactive";
const UNATTENDED = "unattended";
const NONE = "not recorded";

/**
 * The colours, and the words that carry them.
 *
 * `kernel.unattended` for machine work, and the theme has it for exactly this
 * reason — it is not `warning`, because unattended is not a problem, it is a
 * fact about who was there. `kernel.stale` for the rows with no mode, which
 * is the right register: not an error, just older than the field.
 */
const SEGMENTS = [
  {
    key: UNATTENDED,
    label: "no person behind it",
    colour: "kernel.unattended",
    detail:
      "The kernel stamped mode: unattended, which means the token carried no username. Nobody was in front of this write.",
  },
  {
    key: INTERACTIVE,
    label: "a person was there",
    colour: "primary.main",
    detail:
      "The kernel stamped mode: interactive. Somebody was signed in and the write was made for them.",
  },
  {
    key: NONE,
    label: "no mode recorded",
    colour: "kernel.stale",
    detail:
      "Written before LO-55, which is when mode started being stamped. Counted on its own rather than assumed to be either — an absence is not a value.",
  },
];

function modeOf(row) {
  const mode = row.provenance?.mode;
  if (mode === INTERACTIVE || mode === UNATTENDED) return mode;
  return NONE;
}

function tally(rows) {
  const counts = { [INTERACTIVE]: 0, [UNATTENDED]: 0, [NONE]: 0 };
  for (const row of rows) counts[modeOf(row)] += 1;
  return counts;
}

/**
 * The bar.
 *
 * Colour is never the only signal, so every segment is also a labelled chip
 * underneath with its own count — the bar is the shape and the chips are the
 * reading, and somebody who cannot separate the two colours loses nothing.
 */
function Balance({ counts, total, size = "large" }) {
  if (!total) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nothing in this window.
      </Typography>
    );
  }
  return (
    <Stack spacing={size === "large" ? 1.5 : 0.75}>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: size === "large" ? 28 : 10,
          borderRadius: 1,
          overflow: "hidden",
          backgroundColor: "action.hover",
        }}
      >
        {SEGMENTS.map((segment) =>
          counts[segment.key] ? (
            <Tooltip
              key={segment.key}
              title={`${counts[segment.key]} — ${segment.detail}`}
            >
              <Box
                sx={{
                  width: `${(counts[segment.key] / total) * 100}%`,
                  backgroundColor: segment.colour,
                }}
              />
            </Tooltip>
          ) : null,
        )}
      </Box>
      {size === "large" ? (
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
          {SEGMENTS.map((segment) => (
            <Tooltip key={segment.key} title={segment.detail}>
              <Chip
                size="small"
                variant="outlined"
                label={`${counts[segment.key]} ${segment.label}`}
                sx={{
                  color: counts[segment.key] ? segment.colour : "text.disabled",
                  borderColor: counts[segment.key]
                    ? segment.colour
                    : "divider",
                }}
              />
            </Tooltip>
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
}

/**
 * Who wrote, resolved from the envelope rather than from one field of it.
 *
 * **Grouping by `app_type` alone was wrong and the live data said so
 * immediately.** 146 of the first 151 rows carried no `app_type` at all and
 * landed in a bucket labelled "(no app_type recorded)", under a sentence
 * calling them an app's own code. They are `connector:xero`, and a connector
 * is not an app: it holds no manifest, has no Cognito client, and `app_type`
 * is absent because there is nothing for it to name. The field was not lost.
 *
 * So the key is `app_type` when there is one and `actor` when there is not,
 * and the row says which kind it is rather than leaving a reader to work out
 * why one line looks different from the others. `actor` is `app:<instance>`
 * for an app, `connector:<name>` for a connector — the prefix is the answer
 * and it has been in the envelope since LO-40.
 *
 * The three "no person behind it" cases are genuinely different and the row
 * distinguishes them:
 *
 * - **an agent**, named, because a manifest declared it;
 * - **an app's own code**, unattended with no agent — a scheduled job the app
 *   runs itself;
 * - **a connector**, which is neither, and is the largest of the three here.
 */
function SourceRow({ source, rows }) {
  const counts = tally(rows);
  const named = rows.filter((r) => r.provenance?.agent);
  const agents = [...new Set(named.map((r) => r.provenance.agent))].sort();
  const unattendedNoAgent = rows.filter(
    (r) => modeOf(r) === UNATTENDED && !r.provenance?.agent,
  ).length;

  return (
    <Box sx={{ py: 1.5, borderTop: 1, borderColor: "divider", minWidth: 0 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "baseline",
          flexWrap: "wrap",
          justifyContent: "space-between",
          mb: 0.75,
        }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: "baseline", flexWrap: "wrap" }}>
          <Typography variant="mono" component="span" sx={{ fontWeight: 600 }}>
            {source.name}
          </Typography>
          <Tooltip title={source.detail}>
            <Chip size="small" variant="outlined" label={source.kind} />
          </Tooltip>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {rows.length} write{rows.length === 1 ? "" : "s"}
        </Typography>
      </Stack>
      <Balance counts={counts} total={rows.length} size="small" />
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ flexWrap: "wrap", mt: 0.75, alignItems: "center" }}
      >
        {agents.map((agent) => (
          <Chip
            key={agent}
            size="small"
            variant="outlined"
            label={`${agent} · ${
              named.filter((r) => r.provenance.agent === agent).length
            }`}
            sx={{
              fontFamily: "ui-monospace, monospace",
              color: "kernel.unattended",
              borderColor: "kernel.unattended",
            }}
          />
        ))}
        {unattendedNoAgent ? (
          <Tooltip
            title={
              source.kind === "connector"
                ? "A connector is not an agent and not an app: it holds no manifest and no Cognito client, and it is here because it wrote, not because anybody declared it."
                : "An app's own code on a clock is a different fact from an agent, which is why the envelope carries both fields. An absent agent means there was none, not that one could not be identified."
            }
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ borderBottom: "1px dotted", borderColor: "divider", cursor: "help" }}
            >
              {unattendedNoAgent} name no agent —{" "}
              {source.kind === "connector"
                ? "a connector sync"
                : "this app's own code on a clock"}
            </Typography>
          </Tooltip>
        ) : null}
      </Stack>
    </Box>
  );
}

/** What the envelope says wrote this, and which kind of thing that is. */
function sourceOf(row) {
  const provenance = row.provenance || {};
  const actor = String(provenance.actor || "");
  if (provenance.app_type) {
    return {
      key: `app:${provenance.app_type}`,
      name: provenance.app_type,
      kind: "app",
      detail:
        "An app the kernel holds a manifest for. actor is app:<instance>, so two deployments of one app type are two identities and one ceiling.",
    };
  }
  if (actor.startsWith("connector:")) {
    return {
      key: actor,
      name: actor.slice("connector:".length),
      kind: "connector",
      detail:
        "A connector, not an app. It has no manifest and no Cognito client, so app_type is absent rather than missing.",
    };
  }
  return {
    key: actor || "(unknown)",
    name: actor || "(no actor recorded)",
    kind: "unrecognised",
    detail:
      "Neither an app nor a connector by the shape of its actor. Shown as it is rather than filed under a guess.",
  };
}

export default function Attended({ get }) {
  const [page, setPage] = useState(null);
  const [failure, setFailure] = useState(null);

  const load = useCallback(async () => {
    setFailure(null);
    try {
      setPage(await get("/audit", { limit: WINDOW, sort: "desc" }));
    } catch (e) {
      setPage(null);
      setFailure(e);
    }
  }, [get]);

  useEffect(() => {
    load();
  }, [load]);

  const items = useMemo(() => page?.items || [], [page]);

  const bySource = useMemo(() => {
    const groups = new Map();
    for (const row of items) {
      const source = sourceOf(row);
      if (!groups.has(source.key)) groups.set(source.key, { source, rows: [] });
      groups.get(source.key).rows.push(row);
    }
    // Most active first. This is the one ordering on this screen that is not
    // the kernel's, and it is the right one here: the question is where the
    // work is, and alphabetical would bury it.
    return [...groups.values()].sort((a, b) => b.rows.length - a.rows.length);
  }, [items]);

  if (failure) {
    return <Refusal failure={failure} actor="Agent OS" onRetry={load} />;
  }
  if (!page) return <Loading label="Reading who was there" />;

  const counts = tally(items);
  const machine = counts[UNATTENDED];
  const share = items.length
    ? Math.round((machine / items.length) * 100)
    : 0;

  return (
    <Stack spacing={3}>
      {/* The caveat is above the numbers, not below them. A walk that ran out
          of budget changes what every figure on this page means, and a reader
          who meets that after the percentage has already read the percentage. */}
      {page.stopped_early ? (
        <Alert
          severity="warning"
          variant="outlined"
          sx={{ borderColor: "kernel.attention" }}
        >
          <AlertTitle>Read this before the numbers</AlertTitle>
          {page.stopped_detail} Every proportion below is of the{" "}
          {items.length} rows that were returned, not of the log.
        </Alert>
      ) : null}

      {/* This was already the best-argued screen in the product and the
          number it exists to produce was set at `h1` — two points above the
          caption under it. It is the figure now, at the top of the scale,
          because "94% of the work here had nobody in front of it" is the
          single most surprising true thing this platform can say about
          itself. */}
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        <Headline
          eyebrow={`Of the ${items.length} most recent writes`}
          figure={`${share}%`}
          caption="had nobody behind them"
          tone={share >= 50 ? "unattended" : undefined}
          basis={`mode is stamped by the kernel from the shape of the token — a request carrying no username claim had no person in front of it. Counted over the ${items.length} rows this page read, newest first. That is a window on recent activity and not a history: a month of hand-entered work would not appear here if the last few hundred writes were a connector sync.`}
        />
        <Box sx={{ mt: 2.5 }}>
          <Balance counts={counts} total={items.length} />
        </Box>
        {/* One sentence under the number, and the argument for why the number
            is worth anything one click in. It was three lines of prose
            between the headline and the bar it explains, which put the shape
            below the fold on a laptop. */}
        <Box sx={{ mt: 2 }}>
          <Disclose
            summary="The kernel stamps this on every write, from the shape of the token."
            label="Why that matters"
          >
            <Typography variant="body2" color="text.secondary">
              A request carrying no <code>username</code> claim had no person
              in front of it. <code>mode</code> is set by the kernel and{" "}
              <strong>the caller cannot assert it</strong>, which is what makes
              it worth counting — an app cannot describe its own unattended
              work as attended, however it was built.
            </Typography>
          </Disclose>
        </Box>
      </Paper>

      <Box>
        <Typography variant="h2" sx={{ mb: 0.5 }}>
          Where it came from
        </Typography>
        <Disclose
          summary="By what the envelope says wrote it, most active first."
          label="How each one is named"
        >
          <Typography variant="body2" color="text.secondary">
            An app is named by its <code>app_type</code>; a connector has none
            and is named by its <code>actor</code>, because a connector is not
            an app and the field is absent rather than missing. Agents are
            named where one was named — and an unattended write with no agent
            is a third thing again, which each row says.
          </Typography>
        </Disclose>
        <Box sx={{ mt: 1 }}>
          {bySource.map((group) => (
            <SourceRow
              key={group.source.key}
              source={group.source}
              rows={group.rows}
            />
          ))}
        </Box>
      </Box>

      <Divider />


    </Stack>
  );
}
